import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { moveMeeting } from "@/api/lookups";
import { rerunMeeting, resumeMeeting, retryConsensus } from "@/api/meetings";
import { isApiError } from "@/api/errors";
import type {
  AnalysisVersionResponse,
  MeetingCard as MeetingCardDto,
  MeetingRerunRequest,
  UUID,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoveFolderDialog } from "@/components/folder/MoveFolderDialog";
import { RerunDialog } from "@/components/meeting-card/RerunDialog";
import { useCan } from "@/hooks/useCan";
import {
  isLatestVersion,
  isTerminal,
  pickLatest,
  resumeLabelKeyFor,
  versionDateLabel,
  versionModelLabel,
} from "./helpers";

export interface CardHeaderBlockProps {
  card: MeetingCardDto;
  activeVersion: AnalysisVersionResponse | null;
  versions: AnalysisVersionResponse[];
  onChangeVersion: (id: UUID) => void;
}

/**
 * Meeting-card header: filename, status, version picker, action buttons.
 *
 * UI_PLAN §4.2:
 *   - rerun ("Переанализировать") = full `/rerun`, visible only on terminal
 *     latest status (done / partial / error).
 *   - retry ("Повторить"):
 *       · partial ⇒ `/retry-consensus` (phase 5 only)
 *       · error   ⇒ full `/rerun` (same as "Переанализировать", but the
 *         primary single-button affordance for the error state).
 *     Hidden when latest is processing / pending / done.
 */
export function CardHeaderBlock({
  card,
  activeVersion,
  versions,
  onChangeVersion,
}: CardHeaderBlockProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const ownCtx = useMemo(
    () => ({ uploadedBy: card.uploaded_by, participants: card.participants }),
    [card.uploaded_by, card.participants],
  );
  const canRerun = useCan("meeting_rerun", ownCtx);
  const canRetry = useCan("meeting_retry_consensus", ownCtx);
  const canResume = useCan("meeting_resume", ownCtx);
  const latest = pickLatest(versions);
  // ADR-015 toast: 409 from the resume endpoint surfaces here as inline text
  // (no toast library in v1 — see UI_PLAN §10). Cleared on the next click.
  const [resumeError, setResumeError] = useState<string | null>(null);
  /** Move-folder dialog state (folder-management-v3 §3 + §7). */
  const [moveOpen, setMoveOpen] = useState(false);
  /** TM-fe04aa2b — one-off prompt dialog state. */
  const [rerunOpen, setRerunOpen] = useState(false);

  const rerun = useMutation({
    mutationFn: (body?: MeetingRerunRequest) => rerunMeeting(card.id, body),
    onSuccess: () => {
      setRerunOpen(false);
      return queryClient.invalidateQueries({ queryKey: ["meeting", card.id] });
    },
  });
  const retry = useMutation({
    mutationFn: (versionId: UUID) => retryConsensus(versionId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["meeting", card.id] }),
  });
  const resume = useMutation({
    mutationFn: () => resumeMeeting(card.id),
    onSuccess: () => {
      setResumeError(null);
      queryClient.invalidateQueries({ queryKey: ["meeting", card.id] });
    },
    onError: (err) => {
      // 409 = backend determined the partial artifacts can't support a
      // resume; we surface a hint to use the full rerun. Other errors fall
      // through to the same banner so the user is never left guessing.
      if (isApiError(err) && err.status === 409) {
        setResumeError(t("meetings.card.resume.toast.cannotResume"));
        return;
      }
      setResumeError(t("common.errorSave"));
    },
  });

  /**
   * folder-management-v3 §3 — POST /api/meetings/:id/move. On success we
   * invalidate the meeting card and both sides of the move (old + new
   * folder-scoped lists). The MoveFolderDialog surfaces 403/404/validation
   * errors inline; the dialog stays open on error so the user can retry.
   */
  const move = useMutation({
    mutationFn: (next: UUID) => moveMeeting(card.id, next),
    onSuccess: async (updated) => {
      const prev = card.folder_id ?? null;
      const next = updated.folder_id ?? null;
      await queryClient.invalidateQueries({ queryKey: ["meeting", card.id] });
      if (prev !== null) {
        await queryClient.invalidateQueries({
          queryKey: ["meetings", prev],
        });
      }
      if (next !== null) {
        await queryClient.invalidateQueries({
          queryKey: ["meetings", next],
        });
      }
      // Also invalidate the broad ["meetings"] root so the registry view
      // (which keys off it via the active-folder header) refetches.
      await queryClient.invalidateQueries({ queryKey: ["meetings"] });
    },
  });

  const latestStatus = latest?.status ?? null;
  const showRerun = canRerun && isTerminal(latest);
  const showRetry =
    canRetry &&
    latest !== null &&
    (latestStatus === "partial" || latestStatus === "error");
  // ADR-015: resume button is visible on error|partial with a known
  // error_phase >= 3. Phase-1/2 failures have nothing useful to skip (see
  // backend _resume_start_phase); we still show the button on partial
  // (consensus failure) where error_phase may be 5.
  const resumeLabelKey = resumeLabelKeyFor(latest);
  const showResume = canResume && latest !== null && resumeLabelKey !== null;

  const onRetry = () => {
    if (latest === null) return;
    if (latest.status === "partial") {
      retry.mutate(latest.id);
    } else {
      // error => full rerun, same behaviour as Reanalyze. No body, so
      // the BE attaches the active prompt of the meeting type.
      rerun.mutate(undefined);
    }
  };

  // TM-fe04aa2b — pre-fill the dialog textarea with the last run's
  // prompt text so the operator starts from the same baseline they
  // would otherwise re-run. Empty string when there are no versions.
  const lastPromptText = latest?.prompt_text ?? "";

  // TM-556d0f76 — «Скачать» menu. Transcript export needs a non-empty
  // transcript; analysis export needs the *selected* version (activeVersion,
  // not necessarily the latest) to be a readable snapshot (done/partial).
  const hasTranscript =
    card.transcript !== null && card.transcript.text.trim().length > 0;
  const analysisReady =
    activeVersion !== null &&
    (activeVersion.status === "done" || activeVersion.status === "partial");
  const transcriptExportUrl = (timecodes: 0 | 1) =>
    `/api/meetings/${card.id}/transcript/export?timecodes=${timecodes}`;
  const analysisExportUrl = (timecodes: 0 | 1) =>
    activeVersion === null
      ? undefined
      : `/api/meetings/${card.id}/analysis/${activeVersion.id}/export?timecodes=${timecodes}`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle data-testid="card-title">
          <span data-testid="meeting-no" className="text-muted-foreground">
            №{card.meeting_no}
          </span>
          <span aria-hidden="true" className="mx-2 text-muted-foreground">
            ·
          </span>
          <span>{card.original_filename}</span>
        </CardTitle>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="download-menu"
              >
                {t("meetings.card.download")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <a href={card.download_url} data-testid="download-original">
                  {t("meetings.card.downloadOriginal")}
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild disabled={!hasTranscript}>
                <a
                  href={hasTranscript ? transcriptExportUrl(1) : undefined}
                  data-testid="download-transcript-tc"
                >
                  {t("meetings.card.downloadTranscriptTc")}
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild disabled={!hasTranscript}>
                <a
                  href={hasTranscript ? transcriptExportUrl(0) : undefined}
                  data-testid="download-transcript-plain"
                >
                  {t("meetings.card.downloadTranscriptPlain")}
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild disabled={!analysisReady}>
                <a
                  href={analysisReady ? analysisExportUrl(1) : undefined}
                  data-testid="download-analysis-tc"
                >
                  {t("meetings.card.downloadAnalysisTc")}
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild disabled={!analysisReady}>
                <a
                  href={analysisReady ? analysisExportUrl(0) : undefined}
                  data-testid="download-analysis-plain"
                >
                  {t("meetings.card.downloadAnalysisPlain")}
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="move-folder-button"
            aria-label={t("folders.move.buttonAria")}
            onClick={() => setMoveOpen(true)}
          >
            {t("folders.move.button")}
          </Button>
          {versions.length > 0 && activeVersion !== null ? (
            <select
              aria-label={t("meetings.card.versionLabel")}
              data-testid="version-picker"
              // min-w-[18rem] keeps "#4 · OpenAI GPT · 22.05 09:33 (актуальная)"
              // on one line; auto width otherwise truncates inside the select.
              className="h-9 min-w-[18rem] rounded-md border bg-background px-2 text-sm"
              value={activeVersion.id}
              onChange={(e) => onChangeVersion(e.target.value)}
            >
              {versions.map((v) => {
                const isLatest = isLatestVersion(v, versions);
                const label =
                  `#${v.version} · ${versionModelLabel(v, t)}` +
                  ` · ${versionDateLabel(v)}` +
                  (isLatest
                    ? ` (${t("meetings.card.version.latestBadge")})`
                    : "");
                return (
                  <option
                    key={v.id}
                    value={v.id}
                    data-testid="version-picker-option"
                  >
                    {label}
                  </option>
                );
              })}
            </select>
          ) : null}
          {showRerun ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={rerun.isPending}
              onClick={() => setRerunOpen(true)}
              title={t("meetings.card.rerunHint")}
              data-testid="rerun-button"
            >
              {t("meetings.card.rerun")}
            </Button>
          ) : null}
          {showRetry ? (
            <Button
              type="button"
              size="sm"
              disabled={retry.isPending || rerun.isPending}
              onClick={onRetry}
              title={
                latestStatus === "error"
                  ? t("meetings.card.retryErrorHint")
                  : t("meetings.card.retryHint")
              }
            >
              {t("meetings.card.retryConsensus")}
            </Button>
          ) : null}
          {showResume && resumeLabelKey !== null ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              data-testid="resume-button"
              disabled={resume.isPending || retry.isPending || rerun.isPending}
              onClick={() => {
                setResumeError(null);
                resume.mutate();
              }}
              title={t("meetings.card.resume.hint")}
            >
              {t(`meetings.card.resume.fromPhase.${resumeLabelKey}`)}
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          {t(`meetings.card.status.${card.status}`)}
        </p>
        <RerunStats
          rerunCount={card.rerun_count}
          totalCostUsd={card.total_cost_usd}
        />
        {resumeError !== null ? (
          <p
            role="alert"
            data-testid="resume-error"
            className="mt-2 text-sm text-destructive"
          >
            {resumeError}
          </p>
        ) : null}
      </CardContent>
      <MoveFolderDialog
        open={moveOpen}
        currentFolderId={card.folder_id ?? null}
        onMove={(next) => move.mutateAsync(next).then(() => undefined)}
        onClose={() => setMoveOpen(false)}
      />
      <RerunDialog
        open={rerunOpen}
        initialPromptText={lastPromptText}
        isPending={rerun.isPending}
        onSubmit={(payload) => rerun.mutate(payload)}
        onClose={() => setRerunOpen(false)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Rerun counter + total cost (TaskMe 8760a3e1)
// ---------------------------------------------------------------------------

interface RerunStatsProps {
  rerunCount: number | undefined;
  totalCostUsd: string | number | undefined;
}

/**
 * Single-node summary of «N запусков · $X.XX» on the card header.
 *
 * Both halves live in the same DOM element so a single screen-reader
 * announcement covers them (the aria-label carries a longer Russian
 * description). Cost is force-formatted via `Number(value).toFixed(2)`
 * to defend against:
 *   1. Bare floats like `0.1` → render as «$0.10», not «$0.1».
 *   2. FP drift artefacts like `0.27000000001` → render as «$0.27».
 *   3. Backend serialiser flip-flop between Decimal-as-string and float.
 *
 * Render-defensively: when `rerunCount` is missing or zero (contract
 * floor=1, but never trust) we still emit the testid with a sensible
 * fallback so the operator doesn't see a missing widget on a pending
 * meeting before the backend has emitted any cost events.
 */
function RerunStats({ rerunCount, totalCostUsd }: RerunStatsProps) {
  const { t } = useTranslation();
  const rawCount =
    typeof rerunCount === "number" && Number.isFinite(rerunCount)
      ? rerunCount
      : 1;
  const count = rawCount < 1 ? 1 : Math.trunc(rawCount);

  const costNumber =
    totalCostUsd === undefined || totalCostUsd === null
      ? 0
      : Number(totalCostUsd);
  const safeCost = Number.isFinite(costNumber) ? costNumber : 0;
  const costFormatted = safeCost.toFixed(2);

  const runsLabel = t(`meetings.card.rerunStats.${pluralizeRunsKey(count)}`, {
    count,
  });
  const costLabel = t("meetings.card.rerunStats.cost", {
    value: costFormatted,
  });
  const separator = t("meetings.card.rerunStats.separator");

  return (
    <p
      data-testid="meeting-rerun-stats"
      aria-label={t("meetings.card.rerunStats.ariaLabel")}
      className="mt-1 text-xs text-muted-foreground"
    >
      <span>{runsLabel}</span>
      <span aria-hidden="true">{separator}</span>
      <span>{costLabel}</span>
    </p>
  );
}

/**
 * Map an integer count to the Russian pluralization key.
 *
 * Russian noun forms follow the «1 / 2-4 / 5+» split, with an extra
 * clause for the 11-19 teen bucket which always takes the «many» form
 * regardless of the trailing digit (so 11 takes the same form as 5,
 * not 1). Implemented inline rather than via i18next's built-in plural
 * rule because the latter requires `lng=ru` to be live and the test
 * environment bootstraps the bundle in a generic mode — pinning the
 * rule here removes that dependency.
 */
function pluralizeRunsKey(count: number): "runsOne" | "runsFew" | "runsMany" {
  const abs = Math.abs(count);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return "runsOne";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
    return "runsFew";
  return "runsMany";
}
